import { format } from 'date-fns';
import { useBrainStore } from '@/state/brainStore';
import { useSimulationStore } from '@/state/simulationStore';
import { depositPaidAud } from '@/utils/bookingSummary';
import { aud } from '@/utils/format';
import type { Booking, CustomerRecord, Deposit, StudioId } from '@/domain/types';
import { dayKey } from '@/utils/studioDay';
import { customerDepositUrl } from '@/utils/customerLinks';

/**
 * Top-up reminder track (Prompt K, K2). A deposit paid in parts (0 < paid < sent)
 * carries an outstanding balance to chase. This drives an automated reminder
 * cadence surfaced as the confirmations worklist's Top-ups lane and the deposits
 * top-up tracker. Everything is derived from the G2 ledger (`depositPaidAud`) -
 * "partial" is never a stored flag; only the last-reminder timestamp is stored.
 */

// `// ASSUMPTION:` spacing is client-owed - reminder every 7 days while outstanding,
// a final at 7 days before the session, then stop. Kept as named constants.
export const TOPUP_REMINDER_INTERVAL_DAYS = 7;
export const TOPUP_FINAL_REMINDER_DAYS_BEFORE = 7;

export type TopupTrackState = 'due' | 'final-due' | 'waiting' | 'suppressed';

export interface TopupRow {
  depositId: string;
  reference?: string;
  customerId: string;
  customerName: string;
  studioId: StudioId;
  sentAud: number;
  paidAud: number;
  outstandingAud: number;
  sessionIso?: string;
  daysUntilSession?: number;
  lastReminderAt?: string;
  state: TopupTrackState;
  /** True when the cadence says a reminder should go now (and it is not suppressed). */
  dueForReminder: boolean;
}

/** Whole-day gap between two ISO instants, tz-independent (compares the studio day-key at noon UTC). */
function daysBetween(aIso: string, bIso: string): number {
  const a = new Date(`${dayKey(aIso)}T12:00:00Z`).getTime();
  const b = new Date(`${dayKey(bIso)}T12:00:00Z`).getTime();
  return Math.round((a - b) / 86_400_000);
}

function linkedBooking(d: Deposit, bookings: Record<string, Booking>): Booking | undefined {
  if (d.bookingId && bookings[d.bookingId]) return bookings[d.bookingId];
  if (d.heldBookingId && bookings[d.heldBookingId]) return bookings[d.heldBookingId];
  return undefined;
}

/** True when the client spoke last - the ball is in our court, so hold the auto nudge. */
function clientSpokeLast(customer: CustomerRecord | undefined): boolean {
  // An automated system text (B6) doesn't count as us speaking last.
  const conv = (customer?.conversation ?? []).filter((e) => !(e.direction === 'out' && e.authorRole === 'system'));
  return conv.length > 0 && conv[conv.length - 1]!.direction === 'in';
}

export function buildTopupTrack(args: {
  nowIso: string;
  deposits: Record<string, Deposit>;
  bookings: Record<string, Booking>;
  customers: Record<string, CustomerRecord>;
  studioIds?: StudioId[];
}): TopupRow[] {
  const { nowIso, deposits, bookings, customers, studioIds } = args;
  const allow = studioIds && studioIds.length ? new Set(studioIds) : null;
  const rows: TopupRow[] = [];

  for (const d of Object.values(deposits)) {
    if (allow && !allow.has(d.studioId)) continue;
    const paidAud = depositPaidAud(d);
    const sentAud = d.amountAud;
    const outstandingAud = sentAud - paidAud;
    if (paidAud <= 0 || outstandingAud <= 0) continue; // partial only: some money in, some still owed

    const booking = linkedBooking(d, bookings);
    const sessionIso = booking?.start;
    const daysUntilSession = sessionIso != null ? daysBetween(sessionIso, nowIso) : undefined;
    const customer = customers[d.customerId];
    const lastReminderAt = d.lastTopupReminderAt;

    const daysSinceReminder = lastReminderAt ? daysBetween(nowIso, lastReminderAt) : Infinity;
    const intervalElapsed = daysSinceReminder >= TOPUP_REMINDER_INTERVAL_DAYS;
    const inFinalWindow =
      daysUntilSession != null && daysUntilSession >= 0 && daysUntilSession <= TOPUP_FINAL_REMINDER_DAYS_BEFORE;

    let state: TopupTrackState;
    let dueForReminder: boolean;
    if (clientSpokeLast(customer)) {
      state = 'suppressed';
      dueForReminder = false;
    } else if (intervalElapsed && inFinalWindow) {
      state = 'final-due';
      dueForReminder = true;
    } else if (intervalElapsed) {
      state = 'due';
      dueForReminder = true;
    } else {
      state = 'waiting';
      dueForReminder = false;
    }

    rows.push({
      depositId: d.id,
      reference: d.depositReference,
      customerId: d.customerId,
      customerName: customer ? `${customer.firstName} ${customer.lastName}` : (d.depositReference ?? d.id),
      studioId: d.studioId,
      sentAud,
      paidAud,
      outstandingAud,
      sessionIso,
      daysUntilSession,
      lastReminderAt,
      state,
      dueForReminder,
    });
  }

  // Due first, then soonest session.
  return rows.sort(
    (a, b) =>
      Number(b.dueForReminder) - Number(a.dueForReminder) ||
      (a.daysUntilSession ?? 9_999) - (b.daysUntilSession ?? 9_999),
  );
}

/**
 * The reminder copy - restates the outstanding balance + session date (09 Jul copy
 * rule) and carries the REAL tap-to-pay link (tv-2na.8.10: it used to ship a
 * literal "[link]"). `linkUrl` is required so no caller can send a dead link.
 */
export function topupReminderBody(opts: {
  firstName: string;
  outstandingAud: number;
  sessionIso?: string;
  linkUrl: string;
}): string {
  const when = opts.sessionIso ? `the ${format(new Date(opts.sessionIso), 'EEE d MMM')} session` : 'your session';
  return `Hi ${opts.firstName} - a quick reminder that ${aud(opts.outstandingAud)} of your deposit for ${when} is still to come. Tap to top up: ${opts.linkUrl}`;
}

/**
 * Send one top-up reminder (K2): append the SMS to the client's conversation +
 * an activity entry, and stamp `lastTopupReminderAt` so the cadence advances.
 * Same one-tap send shape as J's confirmation texts.
 */
export function sendTopupReminder(depositId: string): void {
  const brain = useBrainStore.getState();
  const now = useSimulationStore.getState().clockIso;
  const deposit = brain.deposits[depositId];
  if (!deposit) return;
  const customer = brain.customers[deposit.customerId];
  const booking = linkedBooking(deposit, brain.bookings);
  const outstandingAud = deposit.amountAud - depositPaidAud(deposit);
  if (outstandingAud <= 0) return;

  const body = topupReminderBody({
    firstName: customer?.firstName ?? 'there',
    outstandingAud,
    sessionIso: booking?.start,
    // The deposit page is plan-aware: it asks for the instalment due now.
    linkUrl: customerDepositUrl(depositId),
  });
  if (customer) {
    brain.addConversationEntry(customer.id, {
      id: `msg-topup-${depositId}-${now}`,
      customerId: customer.id,
      channel: 'sms',
      direction: 'out',
      body,
      timestamp: now,
      authorRole: 'agent',
    });
  }
  brain.appendActivity({
    id: `act-topup-${depositId}-${now}`,
    type: 'sms-sent',
    customerId: deposit.customerId,
    studioId: deposit.studioId,
    body: `Top-up reminder sent - ${aud(outstandingAud)} outstanding.`,
    timestamp: now,
  });
  brain.upsertDeposit({ ...deposit, lastTopupReminderAt: now });
}
